import React, { useState } from 'react';
import { API_BASE_URL } from '../config/platformConfig';
import { XMarkIcon, CheckIcon } from '@heroicons/react/24/outline';
import { getAuth } from 'firebase/auth';

interface DocumentDataExtractionProps {
  applicationId: string;
  documentId: string;
  documentType: string;
  fileUrl: string;
  onClose: () => void;
  onDataApplied: () => void;
}

interface ExtractedData {
  [key: string]: string | null;
}

const FIELD_LABELS: { [key: string]: string } = {
  studentName: 'Student Name',
  studentDOB: 'Date of Birth',
  gender: 'Gender',
  religion: 'Religion',
  motherTongue: 'Mother Tongue',
  aadharNo: 'Aadhar Number',
  parentName: 'Father Name',
  motherName: 'Mother Name',
  parentEmail: 'Email Address',
  parentPhone: 'Phone Number',
  fatherProfession: 'Father Profession',
  applyingForGrade: 'Applying for Grade',
  previousSchool: 'Previous School',
  lastClassAttended: 'Last Class Attended',
  address: 'Present Address'
};

const DocumentDataExtraction: React.FC<DocumentDataExtractionProps> = ({
  applicationId,
  documentId,
  documentType,
  fileUrl,
  onClose,
  onDataApplied
}) => {
  const [loading, setLoading] = useState(false);
  const [extracting, setExtracting] = useState(true);
  const [suggestedData, setSuggestedData] = useState<ExtractedData>({});
  const [confirmedFields, setConfirmedFields] = useState<Set<string>>(new Set());
  const [error, setError] = useState('');
  const [extractedText, setExtractedText] = useState('');
  const [showRawText, setShowRawText] = useState(false);

  React.useEffect(() => {
    extractData();
  }, []);

  const extractData = async () => {
    try {
      setExtracting(true);
      setError('');
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);

      const response = await fetch(`${API_BASE_URL}/applications/${applicationId}/extract-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          applicationId,
          documentId,
          documentType,
          fileUrl
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to extract data');
      }

      setSuggestedData(data.data.suggestedData || {});
      setExtractedText(data.data.extractedText || '');
      
      // Auto-select all extracted fields
      const fields = Object.keys(data.data.suggestedData || {});
      setConfirmedFields(new Set(fields));
    } catch (err: any) {
      setError(err.message || 'Failed to extract data from document');
    } finally {
      setExtracting(false);
    }
  };

  const toggleField = (field: string) => {
    const newConfirmed = new Set(confirmedFields);
    if (newConfirmed.has(field)) {
      newConfirmed.delete(field);
    } else {
      newConfirmed.add(field);
    }
    setConfirmedFields(newConfirmed);
  };

  const handleApplyData = async () => {
    if (confirmedFields.size === 0) {
      setError('Please select at least one field to apply');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);

      const response = await fetch(
        `${API_BASE_URL}/applications/${applicationId}/apply-extracted-data`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            suggestedData,
            confirmedFields: Array.from(confirmedFields)
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to apply data');
      }

      alert(`Successfully updated ${confirmedFields.size} field(s)!`);
      onDataApplied();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to apply data');
    } finally {
      setLoading(false);
    }
  };

  if (extracting) {
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">Extracting Data...</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="ml-4 text-gray-600">Analyzing document with Google Vision API...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Review Extracted Data</h2>
            <p className="text-gray-600 text-sm mt-1">
              Document Type: {documentType.replace('_', ' ')}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded flex items-start gap-3">
            <div className="text-red-700 flex-shrink-0 mt-0.5">⚠️</div>
            <div>
              <p className="font-medium">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {Object.keys(suggestedData).length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
            <div className="h-12 w-12 text-yellow-600 mx-auto mb-3">⚠️</div>
            <p className="text-gray-900 font-medium mb-2">No data could be extracted</p>
            <p className="text-gray-600 text-sm mb-4">
              The document may be unclear or in an unsupported format. You can still manually fill in the application form.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            {/* Extracted Fields */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Extracted Fields ({Object.keys(suggestedData).length})
                </h3>
                <div className="text-sm text-gray-600">
                  Selected: {confirmedFields.size} / {Object.keys(suggestedData).length}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Object.entries(suggestedData).map(([field, value]) => (
                  <div
                    key={field}
                    className={`border rounded-lg p-4 cursor-pointer transition-all ${
                      confirmedFields.has(field)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                    onClick={() => toggleField(field)}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 w-5 h-5 rounded border flex items-center justify-center mt-1 ${
                        confirmedFields.has(field)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-gray-300'
                      }`}>
                        {confirmedFields.has(field) && (
                          <CheckIcon className="h-4 w-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700">
                          {FIELD_LABELS[field] || field}
                        </p>
                        <p className="text-sm text-gray-900 font-semibold mt-1 break-words">
                          {value}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Raw Text Preview */}
            <div className="mb-6 border-t border-gray-200 pt-6">
              <button
                onClick={() => setShowRawText(!showRawText)}
                className="text-sm text-blue-600 hover:text-blue-900 font-medium"
              >
                {showRawText ? '▼ Hide' : '▶ Show'} Raw Extracted Text
              </button>
              {showRawText && (
                <div className="mt-3 bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono">
                    {extractedText}
                  </pre>
                </div>
              )}
            </div>

            {/* Info Box */}
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-900">
                <strong>How it works:</strong> Click on fields to select/deselect them. Only selected fields will be auto-filled in the application form. You can review and edit them afterwards.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 font-medium"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyData}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50"
                disabled={loading || confirmedFields.size === 0}
              >
                {loading ? 'Applying...' : `Apply ${confirmedFields.size} Field(s)`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentDataExtraction;
